/**
 * Supabase Adapter
 * Base44 API'sini taklit eden Supabase tabanlı veri katmanı
 * Production için önerilir
 *
 * Kurulum:
 * npm install @supabase/supabase-js
 *
 * Supabase'de şu tabloları oluşturun:
 * - leads, tasks, products, orders, customer_prices, activity_reports, incoming_offers
 */

import { createClient } from '@supabase/supabase-js';

// Supabase config - .env dosyasından alınmalı
const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;

let supabase = null;

/**
 * Supabase client'ı başlatır
 */
export function initSupabase(url = SUPABASE_URL, key = SUPABASE_ANON_KEY) {
  if (!url || !key) {
    console.warn('Supabase URL veya Key tanımlı değil. .env dosyasını kontrol edin.');
    return null;
  }
  if (!supabase) {
    supabase = createClient(url, key);
  }
  return supabase;
}

/**
 * Aktif oturumun access token'ını döndürür.
 * Serverless proxy'lere (shopify/maps/admin-users) Authorization
 * header'ı eklemek için kullanılır.
 * @returns {Promise<string|null>}
 */
export async function getAccessToken() {
  if (!supabase) initSupabase();
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

/**
 * Oturum doğrulamalı serverless admin endpoint çağrısı.
 * @param {string} action - 'create' | 'set-password' | 'delete'
 * @param {object} payload
 */
async function callAdminUsersApi(action, payload = {}) {
  const token = await getAccessToken();
  if (!token) throw new Error('Keine aktive Sitzung. Bitte neu anmelden.');

  const res = await fetch('/api/admin-users', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ action, ...payload }),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.error || `Admin API Fehler (${res.status})`);
  }
  return json;
}

/**
 * Entity adını tablo adına çevirir (camelCase -> snake_case + çoğul)
 */
function entityToTable(entityName) {
  const tableMap = {
    Lead: 'leads',
    Task: 'tasks',
    Product: 'products',
    Order: 'orders',
    CustomerPrice: 'customer_prices',
    ActivityReport: 'activity_reports',
    IncomingOffer: 'incoming_offers',
    Agent: 'agents',
    PdfSetting: 'pdf_settings',
    Notification: 'notifications',
    PurchaseOrder: 'purchase_orders',
    StockMovement: 'stock_movements',
    Gutschrift: 'gutschriften',
    ProductVariant: 'product_variants',
    ProductImage: 'product_images',
    Supplier: 'suppliers',
    EwkKategorie: 'ewk_kategorien',
  };
  return tableMap[entityName] || entityName.toLowerCase() + 's';
}

/**
 * Supabase Entity sınıfı
 */
class SupabaseEntity {
  constructor(name) {
    this.name = name;
    this.table = entityToTable(name);
  }

  /**
   * Supabase client'ı kontrol eder
   */
  getClient() {
    if (!supabase) {
      throw new Error('Supabase başlatılmadı. initSupabase() çağırın.');
    }
    return supabase;
  }

  /**
   * Tüm kayıtları listeler
   * Supabase tek sorguda max 1000 satır döndürür — 1000'lik
   * sayfalarla tamamını çeker (ör. tasks > 2500 kayıt).
   */
  async list(sortField = null) {
    const client = this.getClient();
    const PAGE = 1000;
    const all = [];

    for (let from = 0; ; from += PAGE) {
      let query = client.from(this.table).select('*').range(from, from + PAGE - 1);

      if (sortField) {
        const desc = sortField.startsWith('-');
        const field = desc ? sortField.slice(1) : sortField;
        query = query.order(field, { ascending: !desc });
      }
      // Stabil sayfalama: eşit sıralama değerlerinde satır kaymasını önler
      query = query.order('id', { ascending: true });

      const { data, error } = await query;
      if (error) throw new Error(error.message);
      all.push(...(data || []));
      if (!data || data.length < PAGE) break;
    }

    return all;
  }

  /**
   * Filtrelenmiş kayıtları döndürür
   */
  async filter(filters = {}, sortField = null) {
    const client = this.getClient();
    const PAGE = 1000;
    const all = [];

    for (let from = 0; ; from += PAGE) {
      let query = client.from(this.table).select('*').range(from, from + PAGE - 1);

      // Filtreleri uygula
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            query = query.in(key, value);
          } else {
            query = query.eq(key, value);
          }
        }
      });

      // Sıralama
      if (sortField) {
        const desc = sortField.startsWith('-');
        const field = desc ? sortField.slice(1) : sortField;
        query = query.order(field, { ascending: !desc });
      }
      // Stabil sayfalama: eşit sıralama değerlerinde satır kaymasını önler
      query = query.order('id', { ascending: true });

      const { data, error } = await query;
      if (error) throw new Error(error.message);
      all.push(...(data || []));
      if (!data || data.length < PAGE) break;
    }

    return all;
  }

  /**
   * Tek kayıt getirir
   */
  async get(id) {
    const client = this.getClient();
    const { data, error } = await client
      .from(this.table)
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw new Error(error.message);
    return data;
  }

  /**
   * Yeni kayıt oluşturur
   */
  async create(record) {
    const client = this.getClient();
    const { data, error } = await client
      .from(this.table)
      .insert([{
        ...record,
        created_date: new Date().toISOString(),
      }])
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  }

  /**
   * Kayıt günceller
   */
  async update(id, updates) {
    const client = this.getClient();
    const { data, error } = await client
      .from(this.table)
      .update({
        ...updates,
        updated_date: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  }

  /**
   * Kayıt siler
   */
  async delete(id) {
    const client = this.getClient();
    const { error } = await client
      .from(this.table)
      .delete()
      .eq('id', id);

    if (error) throw new Error(error.message);
    return { success: true };
  }

  /**
   * Toplu kayıt oluşturur
   */
  async createMany(records) {
    const client = this.getClient();
    const { data, error } = await client
      .from(this.table)
      .insert(records.map(r => ({
        ...r,
        created_date: new Date().toISOString(),
      })))
      .select();

    if (error) throw new Error(error.message);
    return data;
  }

  /**
   * Kayıt sayısını döndürür
   */
  async count(filters = {}) {
    const client = this.getClient();
    let query = client.from(this.table).select('*', { count: 'exact', head: true });

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        query = query.eq(key, value);
      }
    });

    const { count, error } = await query;

    if (error) throw new Error(error.message);
    return count || 0;
  }

  /**
   * Upsert — insert or update on column conflict
   */
  async upsert(record, onConflictCols) {
    const client = this.getClient();
    const { data, error } = await client
      .from(this.table)
      .upsert([{ ...record }], {
        onConflict: onConflictCols,
        ignoreDuplicates: false,
      })
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  }
}

/**
 * Agent entity — silme işlemi auth kullanıcısını da kaldırmak için
 * service-role korumalı admin endpoint'i üzerinden yapılır.
 */
class AgentEntity extends SupabaseEntity {
  async delete(id) {
    await callAdminUsersApi('delete', { agentId: id });
    return { success: true };
  }
}

const agentEntity = new AgentEntity('Agent');

/**
 * Supabase Adapter
 */
export const SupabaseAdapter = {
  entities: {
    Lead: new SupabaseEntity('Lead'),
    Task: new SupabaseEntity('Task'),
    Product: new SupabaseEntity('Product'),
    Order: new SupabaseEntity('Order'),
    CustomerPrice: new SupabaseEntity('CustomerPrice'),
    ActivityReport: new SupabaseEntity('ActivityReport'),
    IncomingOffer: new SupabaseEntity('IncomingOffer'),
    Agent: agentEntity,
    User: agentEntity, // Alias User to Agent table
    PdfSetting: new SupabaseEntity('PdfSetting'),
    Notification: new SupabaseEntity('Notification'),
    PurchaseOrder: new SupabaseEntity('PurchaseOrder'),
    StockMovement: new SupabaseEntity('StockMovement'),
    Gutschrift: new SupabaseEntity('Gutschrift'),
    ProductVariant: new SupabaseEntity('ProductVariant'),
    ProductImage: new SupabaseEntity('ProductImage'),
    Supplier: new SupabaseEntity('Supplier'),
    EwkKategorie: new SupabaseEntity('EwkKategorie'),
  },

  /**
   * User management
   */
  users: {
    /**
     * Yeni kullanıcı oluşturur — Supabase Auth + agents satırı,
     * service-role korumalı /api/admin-users endpoint'i üzerinden.
     * Şifre plaintext olarak DB'ye YAZILMAZ (Supabase Auth hash'ler).
     */
    async inviteUser(email, role, { vorname, nachname, passwort } = {}) {
      const result = await callAdminUsersApi('create', {
        email,
        rolle: role,
        vorname: vorname || '',
        nachname: nachname || '',
        password: passwort,
      });
      return result.agent;
    },

    async updateAgent(agentId, updates) {
      if (!supabase) throw new Error('Supabase client not initialized');

      // Şifre değişikliği → Supabase Auth (admin endpoint, hash'lenir)
      if (updates.passwort) {
        await callAdminUsersApi('set-password', {
          agentId,
          password: updates.passwort,
        });
      }

      // Build update payload — only include non-empty fields
      const payload = {};
      if (updates.vorname !== undefined) payload.vorname = updates.vorname;
      if (updates.nachname !== undefined) payload.nachname = updates.nachname;
      if (updates.vorname || updates.nachname) {
        payload.name = [updates.vorname, updates.nachname].filter(Boolean).join(' ');
      }
      if (updates.rolle) payload.rolle = updates.rolle;
      if (updates.gebiet) payload.gebiet = updates.gebiet;
      if (updates.telefon) payload.telefon = updates.telefon;
      if (updates.position) payload.position = updates.position;
      // screen_permissions JSONB — boş obje de kabul et (override sıfırlama)
      if (updates.screen_permissions !== undefined) payload.screen_permissions = updates.screen_permissions;

      // Sadece şifre değiştiyse tablo update'ine gerek yok
      if (Object.keys(payload).length === 0) {
        const { data, error } = await supabase
          .from('agents')
          .select('*')
          .eq('id', agentId)
          .single();
        if (error) throw new Error(error.message);
        return data;
      }

      const { data, error } = await supabase
        .from('agents')
        .update(payload)
        .eq('id', agentId)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    }
  },

  /**
   * Supabase client'ı döndürür (auth vb. için)
   */
  getClient() {
    return supabase;
  },

  /**
   * Auth listener
   */
  onAuthStateChange(callback) {
    if (!supabase) return null;
    return supabase.auth.onAuthStateChange(callback);
  },
};

export default SupabaseAdapter;
